import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { Satellite, LogOut, Menu, X, ChevronRight } from "lucide-react";
import { getStoredUser, logoutAndRedirect } from "@/lib/api";

export default function DashLayout({ navItems, title, basePath, children }) {
  const user = getStoredUser();
  const navigate = useNavigate();
  const loc = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Build groups once from navItems
  const groups = useMemo(() => {
    const g = [];
    let cur = null;
    navItems.forEach((it) => {
      if (it.group && (!cur || cur.label !== it.group)) {
        cur = { label: it.group, items: [it] };
        g.push(cur);
      } else if (it.group && cur && cur.label === it.group) {
        cur.items.push(it);
      } else {
        g.push({ label: null, items: [it] });
        cur = null;
      }
    });
    return g;
  }, [navItems]);

  // Auto-expand the group that contains the active route
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = new Set();
    groups.forEach((g) => {
      if (!g.label) return;
      const isActive = g.items.some((it) => {
        const abs = it.to === "." || it.to === "" ? basePath : `${basePath}/${it.to}`;
        return loc.pathname === abs || loc.pathname.startsWith(abs + "/");
      });
      if (isActive) initial.add(g.label);
    });
    return initial;
  });

  // When route changes, auto-expand the group that contains the new active route
  useEffect(() => {
    groups.forEach((g) => {
      if (!g.label) return;
      const isActive = g.items.some((it) => {
        const abs = it.to === "." || it.to === "" ? basePath : `${basePath}/${it.to}`;
        return loc.pathname === abs || loc.pathname.startsWith(abs + "/");
      });
      if (isActive) setExpandedGroups((prev) => new Set([...prev, g.label]));
    });
  }, [loc.pathname]);

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [loc.pathname]);

  // Close sidebar on Escape key
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") setSidebarOpen(false); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const logout = () => {
    logoutAndRedirect();
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center gap-2 px-6 border-b border-white/10 shrink-0">
        <div className="w-8 h-8 rounded-md bg-gradient-to-br from-brand-primary to-brand-accent flex items-center justify-center">
          <Satellite className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold">DishLCO</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">{title}</div>
        </div>
        {/* Close button - mobile only */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-1 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close sidebar"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        {groups.map((g, gi) => {
          if (!g.label) {
            // Ungrouped items (e.g. Dashboard) — render directly
            return g.items.map((it) => {
              const absolutePath = it.to === "." || it.to === "" ? basePath : `${basePath}/${it.to}`;
              const Icon = it.icon;
              return (
                <NavLink key={it.to} to={absolutePath} end={it.end}
                      data-testid={`nav-${it.label.toLowerCase().replace(/\W+/g, "-")}`}
                      className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all ${isActive ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
                  <Icon className="w-4 h-4 shrink-0" /> <span className="truncate">{it.label}</span>
                </NavLink>
              );
            });
          }
          const isOpen = expandedGroups.has(g.label);
          const hasActive = g.items.some((it) => {
            const abs = it.to === "." || it.to === "" ? basePath : `${basePath}/${it.to}`;
            return loc.pathname === abs || loc.pathname.startsWith(abs + "/");
          });
          return (
            <div key={gi} className="mt-2 first:mt-0">
              <button
                type="button"
                onClick={() => toggleGroup(g.label)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.14em] font-semibold transition-colors select-none ${
                  hasActive ? "text-white/70" : "text-white/40 hover:text-white/60"
                }`}
              >
                <span>{g.label}</span>
                <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
              </button>
              <div
                className="overflow-hidden transition-all duration-200 ease-in-out"
                style={{
                  maxHeight: isOpen ? `${g.items.length * 40}px` : "0px",
                  opacity: isOpen ? 1 : 0,
                }}
              >
                <div className="space-y-0.5 pl-1">
                  {g.items.map((it) => {
                    const absolutePath = it.to === "." || it.to === "" ? basePath : `${basePath}/${it.to}`;
                    const Icon = it.icon;
                    return (
                      <NavLink key={it.to} to={absolutePath} end={it.end}
                            data-testid={`nav-${it.label.toLowerCase().replace(/\W+/g, "-")}`}
                            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all ${isActive ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
                        <Icon className="w-4 h-4 shrink-0" /> <span className="truncate">{it.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      {/* User info + logout */}
      <div className="p-3 border-t border-white/10 shrink-0">
        <div className="px-3 py-2 text-xs text-white/60">
          <div className="font-semibold text-white truncate">{user?.name}</div>
          <div className="font-mono truncate">{user?.email}</div>
        </div>
        <button onClick={logout} data-testid="logout-button"
                className="w-full mt-1 flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded-md transition-all">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-surface-bg">
      {/* Backdrop overlay - mobile only */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - desktop: always visible, mobile: slide-in */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-72 bg-brand-nav text-white flex flex-col scrollbar-thin
          transform transition-transform duration-300 ease-in-out
          lg:relative lg:translate-x-0 lg:w-64 lg:z-auto
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        `}
        data-testid="sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-auto">
        {/* Top bar */}
        <div className="h-14 sm:h-16 border-b border-slate-200 bg-white/90 backdrop-blur px-4 sm:px-6 md:px-8 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            {/* Hamburger button - mobile only */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-1.5 -ml-1 rounded-md text-slate-600 hover:text-brand-nav hover:bg-slate-100 transition-colors"
              aria-label="Open sidebar"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="font-heading font-bold text-lg sm:text-xl text-brand-nav truncate">{title}</h1>
          </div>
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-brand-primary shrink-0">{user?.role}</span>
        </div>

        {/* Page content */}
        <div className="p-4 sm:p-6 md:p-8">{children}</div>
      </main>
    </div>
  );
}

export function StatCard({ label, value, hint, accent = "primary", testid }) {
  const colors = { primary: "text-brand-primary", accent: "text-brand-accent", nav: "text-brand-nav", amber: "text-amber-500", danger: "text-red-500" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      <div className={`mt-1.5 sm:mt-2 font-heading text-2xl sm:text-3xl font-bold tracking-tighter ${colors[accent]}`}>{value}</div>
      {hint && <div className="text-[10px] sm:text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

export function Badge({ children, tone = "neutral" }) {
  const tones = {
    success: "bg-brand-accent/10 text-brand-accent border border-brand-accent/20",
    info: "bg-brand-primary/10 text-brand-primary border border-brand-primary/20",
    warn: "bg-amber-50 text-amber-700 border border-amber-200",
    danger: "bg-red-50 text-red-700 border border-red-200",
    neutral: "bg-slate-100 text-slate-600 border border-slate-200",
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tones[tone]}`}>{children}</span>;
}
